/**
 * ============================================================================
 * MAIN.JS - PUNTO DE ENTRADA DE LA APLICACION VUE
 * ============================================================================
 *
 * Este archivo es el PRIMER archivo JavaScript que se ejecuta cuando la
 * aplicacion Vue se inicia. Es como la "puerta principal" de tu aplicacion.
 *
 * PROPOSITO:
 * - Crear la instancia principal de Vue
 * - Registrar todos los plugins (Vuetify, Router, Store)
 * - Montar la aplicacion en el DOM (en el elemento #app del index.html)
 * - Manejar errores globales conocidos
 *
 * FLUJO DE EJECUCION:
 * 1. El navegador carga index.html
 * 2. index.html tiene un <div id="app"></div> vacio
 * 3. Se ejecuta este archivo (main.js)
 * 4. Vue toma control del div#app y renderiza toda la aplicacion
 *
 * RELACION CON OTROS ARCHIVOS:
 * - App.vue: El componente raiz que se renderiza
 * - router/index.js: Define las rutas de navegacion
 * - store/index.js: El estado global de la aplicacion (Vuex)
 * - plugins/vuetify.js: Configuracion del framework de UI
 * ============================================================================
 */

// ============================================================================
// IMPORTACIONES
// ============================================================================

/**
 * createApp: Funcion de Vue 3 para crear una nueva instancia de aplicacion.
 *
 * En Vue 2 se usaba: new Vue({...})
 * En Vue 3 se usa: createApp(...)
 *
 * Esta funcion devuelve un objeto "app" que tiene metodos como:
 * - app.use() para registrar plugins
 * - app.mount() para montar en el DOM
 * - app.component() para registrar componentes globales
 */
import { createApp } from 'vue'

/**
 * App.vue: El componente RAIZ de toda la aplicacion.
 *
 * Piensa en App.vue como el "contenedor principal" que envuelve
 * TODO lo demas. Todos los otros componentes se renderizan DENTRO
 * de App.vue, ya sea directamente o a traves del <router-view>.
 *
 * Jerarquia:
 *   App.vue
 *     └── MainLayout.vue (o BlankLayout para login)
 *           └── <router-view /> (aqui se renderizan las vistas)
 *                 └── DashboardView.vue, FacturaView.vue, etc.
 */
import App from './App.vue'

/**
 * router: El sistema de navegacion de Vue Router.
 *
 * Importamos la configuracion desde ./router/index.js
 *
 * Vue Router permite:
 * - Navegar entre paginas SIN recargar el navegador
 * - Definir que componente se muestra segun la URL
 * - Proteger rutas con guardias (ej: requiere autenticacion)
 * - Pasar parametros en la URL (ej: /factura/123)
 *
 * Ejemplo de como funciona:
 *   URL: /dashboard  ->  Renderiza: DashboardView.vue
 *   URL: /login      ->  Renderiza: LoginView.vue
 *   URL: /emision/factura -> Renderiza: FacturaView.vue
 */
import router from './router'

/**
 * store: El estado global de la aplicacion (Vuex).
 *
 * Importamos la configuracion desde ./store/index.js
 *
 * Vuex es como una "base de datos en memoria" para tu frontend.
 * Guarda datos que necesitan ser accesibles desde CUALQUIER componente:
 * - Usuario autenticado
 * - Catalogos del Ministerio de Hacienda
 * - Lista de documentos emitidos
 * - Sucursal seleccionada
 *
 * Sin Vuex, tendrias que pasar datos de componente en componente
 * (props drilling), lo cual es muy tedioso en apps grandes.
 *
 * Ejemplo:
 *   // En cualquier componente puedes acceder a:
 *   this.$store.state.auth.user  // Usuario logueado
 *   this.$store.getters['catalogos/formasPago']  // Formas de pago
 */
import store from './store'

/**
 * vuetify: El framework de UI basado en Material Design.
 *
 * Importamos la configuracion desde ./plugins/vuetify.js
 *
 * Vuetify proporciona componentes pre-diseñados como:
 * - v-btn (botones)
 * - v-card (tarjetas)
 * - v-text-field (campos de texto)
 * - v-data-table (tablas)
 * - v-dialog (modales)
 * - etc.
 *
 * Estos componentes siguen las guias de Material Design de Google
 * y son completamente responsivos (se adaptan a movil/desktop).
 */
import vuetify from './plugins/vuetify'

/**
 * Estilos globales CSS personalizados.
 *
 * Importar un archivo CSS en JavaScript hace que webpack lo procese
 * y lo incluya en el bundle final. Estos estilos se aplicaran
 * a TODA la aplicacion.
 *
 * Aqui puedes definir:
 * - Variables CSS personalizadas
 * - Estilos base para elementos HTML
 * - Clases utilitarias globales
 * - Overrides de estilos de Vuetify
 */
import './assets/styles/main.css'


// ============================================================================
// MANEJO DE ERRORES DE RESIZEOBSERVER
// ============================================================================
/**
 * PROBLEMA: ResizeObserver loop limit exceeded
 *
 * Este es un error BENIGNO (no afecta la funcionalidad) que ocurre
 * frecuentemente con Vuetify, especialmente con componentes como:
 * - v-autocomplete
 * - v-select
 * - v-data-table
 * - v-virtual-scroll
 *
 * POR QUE OCURRE:
 * ResizeObserver es una API del navegador que notifica cuando un elemento
 * cambia de tamaño. Vuetify usa esto para ajustar layouts dinamicamente.
 *
 * El error ocurre cuando:
 * 1. Un elemento cambia de tamaño
 * 2. El callback de ResizeObserver modifica el DOM
 * 3. Esto causa otro cambio de tamaño
 * 4. Se crea un "loop" de observaciones
 *
 * El navegador detecta esto y lanza el error para evitar loops infinitos.
 * PERO en Vuetify, estos "loops" son intencionales y se estabilizan solos.
 *
 * SOLUCION:
 * Simplemente suprimimos el error porque:
 * 1. No afecta la funcionalidad
 * 2. Es esperado en Vuetify
 * 3. Se estabiliza en microsegundos
 *
 * Usamos 3 metodos diferentes para asegurarnos de capturarlo:
 */

/**
 * Expresiones regulares para detectar el error de ResizeObserver.
 *
 * resizeObserverLoopErr: Patron que NO coincide con el mensaje
 * resizeObserverLoopErr2: Patron que SI coincide con "ResizeObserver loop"
 *
 * El mensaje de error tipico es:
 * "ResizeObserver loop limit exceeded" o
 * "ResizeObserver loop completed with undelivered notifications"
 */
const resizeObserverLoopErr = /^[^(ResizeObserver loop limit exceeded)]/
const resizeObserverLoopErr2 = /ResizeObserver loop/

/**
 * METODO 1: Sobreescribir window.onerror
 *
 * window.onerror es el manejador global de errores del navegador.
 * Cuando ocurre CUALQUIER error no capturado, se llama a esta funcion.
 *
 * Parametros:
 * - message: El mensaje de error
 * - source: URL del script donde ocurrio
 * - lineno: Numero de linea
 * - colno: Numero de columna
 * - error: El objeto Error completo
 *
 * Si retornamos TRUE, el error se considera "manejado" y no se
 * muestra en la consola.
 */
const originalOnError = window.onerror
window.onerror = function (message, source, lineno, colno, error) {
  // Si el mensaje contiene "ResizeObserver loop", lo ignoramos
  if (message && typeof message === 'string' && resizeObserverLoopErr2.test(message)) {
    return true // Error "manejado", no mostrar en consola
  }
  // Si habia un manejador original, lo llamamos
  if (originalOnError) {
    return originalOnError(message, source, lineno, colno, error)
  }
  return false // Error no manejado, mostrar en consola
}

/**
 * METODO 2: Event Listener con captura
 *
 * Agregamos un listener al evento 'error' de window.
 * El tercer parametro TRUE significa "usar fase de captura".
 *
 * Fases de eventos en JavaScript:
 * 1. CAPTURA: El evento viaja desde window hacia el elemento
 * 2. TARGET: El evento llega al elemento que lo genero
 * 3. BURBUJA: El evento sube de vuelta hacia window
 *
 * Usar captura nos permite interceptar el evento ANTES de que
 * cualquier otro handler lo procese.
 *
 * stopImmediatePropagation(): Detiene completamente el evento
 * stopPropagation(): Evita que suba/baje a otros elementos
 * preventDefault(): Cancela la accion por defecto
 */
window.addEventListener('error', (e) => {
  if (e.message && resizeObserverLoopErr2.test(e.message)) {
    e.stopImmediatePropagation()
    e.stopPropagation()
    e.preventDefault()
    return true
  }
}, true) // true = fase de captura

/**
 * METODO 3: Parchar ResizeObserver directamente
 *
 * Esta es la solucion mas robusta: modificamos la clase ResizeObserver
 * para que use requestAnimationFrame.
 *
 * requestAnimationFrame:
 * - Programa la ejecucion para el proximo "frame" de renderizado
 * - Tipicamente 60 veces por segundo (cada ~16ms)
 * - Evita que multiples cambios de tamaño se procesen en el mismo ciclo
 *
 * Al envolver el callback en requestAnimationFrame, le damos tiempo
 * al navegador de estabilizar el layout antes de procesar cambios.
 *
 * COMO FUNCIONA:
 * 1. Guardamos la clase original en OriginalResizeObserver
 * 2. Creamos una nueva clase que extiende la original
 * 3. En el constructor, envolvemos el callback con requestAnimationFrame
 * 4. Reemplazamos window.ResizeObserver con nuestra version
 */
const OriginalResizeObserver = window.ResizeObserver
window.ResizeObserver = class ResizeObserver extends OriginalResizeObserver {
  constructor(callback) {
    // Llamamos al constructor padre pero con un callback modificado
    super((entries, observer) => {
      // Programamos la ejecucion para el proximo frame
      window.requestAnimationFrame(() => {
        try {
          // Ejecutamos el callback original
          callback(entries, observer)
        } catch (e) {
          // Silenciamos cualquier error de ResizeObserver
          // Esto es seguro porque son errores benignos
        }
      })
    })
  }
}


// ============================================================================
// CREACION Y CONFIGURACION DE LA APLICACION VUE
// ============================================================================

/**
 * Crear la instancia de la aplicacion Vue.
 *
 * createApp(App) crea una nueva aplicacion Vue usando App.vue
 * como el componente raiz.
 *
 * Esta linea NO renderiza nada todavia, solo crea el objeto
 * de configuracion de la aplicacion.
 *
 * El objeto 'app' retornado tiene metodos para:
 * - Registrar plugins: app.use(plugin)
 * - Registrar componentes globales: app.component('nombre', Componente)
 * - Registrar directivas: app.directive('nombre', directiva)
 * - Montar en el DOM: app.mount('#app')
 */
const app = createApp(App)

/**
 * Registrar el Store de Vuex.
 *
 * app.use(store) hace que el store este disponible en:
 *
 * 1. Options API (Vue 2 style):
 *    this.$store.state.auth.user
 *    this.$store.dispatch('auth/login', data)
 *    this.$store.commit('auth/SET_USER', user)
 *
 * 2. Composition API (Vue 3 style):
 *    import { useStore } from 'vuex'
 *    const store = useStore()
 *    store.state.auth.user
 *    store.dispatch('auth/login', data)
 *
 * IMPORTANTE: El orden de los .use() generalmente no importa,
 * pero es buena practica poner store y router primero porque
 * otros plugins podrian depender de ellos.
 */
app.use(store)

/**
 * Registrar Vue Router.
 *
 * app.use(router) hace que el router este disponible y ademas:
 *
 * 1. Registra los componentes <router-view> y <router-link>
 * 2. Hace disponible this.$router y this.$route
 * 3. Activa la navegacion basada en la URL actual
 *
 * Despues de esto puedes usar:
 * - <router-view />: Renderiza el componente de la ruta actual
 * - <router-link to="/ruta">Ir</router-link>: Link de navegacion
 * - this.$router.push('/ruta'): Navegar programaticamente
 * - this.$route.params: Obtener parametros de la URL
 */
app.use(router)

/**
 * Registrar Vuetify.
 *
 * app.use(vuetify) registra todos los componentes de Vuetify
 * y aplica el tema configurado en ./plugins/vuetify.js
 *
 * Despues de esto puedes usar componentes como:
 * - <v-app>: Contenedor principal (OBLIGATORIO)
 * - <v-btn>: Botones
 * - <v-card>: Tarjetas
 * - <v-text-field>: Campos de texto
 * - <v-data-table>: Tablas con paginacion y ordenamiento
 * - etc.
 *
 * NOTA: Tu App.vue DEBE tener <v-app> como elemento raiz
 * para que Vuetify funcione correctamente.
 */
app.use(vuetify)

/**
 * Restaurar sesión ANTES de montar la aplicación.
 *
 * Esto asegura que cuando los componentes se rendericen por primera vez,
 * ya tengan acceso a los datos del usuario (permisos, rol, etc.).
 *
 * Sin esto, los componentes se renderizan con permisos vacíos y aunque
 * la reactividad debería actualizarlos, puede causar problemas visuales
 * donde los menús aparecen vacíos brevemente.
 */
store.dispatch('auth/restoreSession')

/**
 * Montar la aplicacion en el DOM.
 *
 * app.mount('#app') busca en el HTML un elemento con id="app"
 * y REEMPLAZA su contenido con la aplicacion Vue renderizada.
 *
 * En index.html tienes algo como:
 *   <body>
 *     <div id="app"></div>
 *   </body>
 *
 * Despues de mount(), Vue toma control de ese div y renderiza
 * App.vue dentro de el. El div original desaparece.
 *
 * IMPORTANTE: mount() debe llamarse DESPUES de todos los .use()
 * porque una vez montada, la aplicacion comienza a funcionar
 * y necesita todos los plugins disponibles.
 *
 * Retorna la instancia del componente raiz, que rara vez necesitas.
 */
app.mount('#app')
